import React, { useState, createContext, useContext, useEffect } from "react";
import { useApiService } from "../shared/apiservice";
import {
    Attachment,
    NonConformanceProductRef,
    NonConformanceReason,
    NonConformanceReport,
    NonConformanceReportItem,
    NonConformanceReportListItem,
} from "../shared/types";

export type SortOrder = "asc" | "desc";

const REASON_VALUES: NonConformanceReason[] = ["damaged", "missing", "incorrect", "other"];

function parseReason(value: unknown): NonConformanceReason {
    if (typeof value === "string" && REASON_VALUES.includes(value as NonConformanceReason)) {
        return value as NonConformanceReason;
    }
    return "other";
}

function parseProductRef(p: any): NonConformanceProductRef {
    return {
        id: p.id,
        name: p.name ?? "",
        code: p.code ?? p.main_code,
    };
}

function parseAttachment(a: any, index: number): Attachment {
    return {
        id: typeof a?.id === "number" ? a.id : index,
        key: typeof a?.key === "string" ? a.key : String(a?.key ?? ""),
        status: typeof a?.status === "string" ? a.status : String(a?.status ?? ""),
        mime_type:
            typeof a?.mime_type === "string"
                ? a.mime_type
                : typeof a?.mimeType === "string"
                    ? a.mimeType
                    : "",
        filename: typeof a?.filename === "string" ? a.filename : String(a?.filename ?? ""),
        size: typeof a?.size === "number" ? a.size : Number(a?.size ?? 0),
        url: typeof a?.url === "string" ? a.url : undefined,
    };
}

function parseReportListItem(d: any): NonConformanceReportListItem {
    const rawDate = d.created_at ?? d.date ?? d.submitted_at;
    return {
        id: d.id,
        date: rawDate ? new Date(rawDate) : new Date(),
        supplier:
            typeof d.supplier === "string"
                ? d.supplier
                : d.supplier?.name ?? "",
        reference: d.reference ?? "",
        user: {
            id: d.user?.id,
            email: d.user?.email,
            functions: d.user?.functions,
            name: d.user?.name ?? "—",
        },
    };
}

function parseReportItem(d: any, index: number): NonConformanceReportItem {
    return {
        id: typeof d.id === "number" ? d.id : index,
        product: parseProductRef(d.product ?? {}),
        reason: parseReason(d.reason),
        notes: d.notes != null ? String(d.notes) : "",
        attachments: Array.isArray(d.attachments)
            ? d.attachments.map((a: any, i: number) => parseAttachment(a, i))
            : [],
    };
}

function parseReport(d: any): NonConformanceReport {
    const rawDate = d.created_at ?? d.date ?? d.submitted_at;
    const notesRaw = d.notes;
    const notes =
        notesRaw == null || String(notesRaw).trim() === "" ? null : String(notesRaw);

    return {
        id: d.id,
        date: rawDate ? new Date(rawDate) : new Date(),
        supplier:
            typeof d.supplier === "string"
                ? d.supplier
                : d.supplier?.name ?? "",
        reference: d.reference ?? "",
        user: {
            id: d.user_id,
            email: d.user?.email,
            functions: d.user?.functions,
            name: d.user?.name ?? "—",
        },
        notes,
        items: Array.isArray(d.items)
            ? d.items.map((item: any, i: number) => parseReportItem(item, i))
            : [],
    };
}

const reportsListContext = createContext<{
    reports: NonConformanceReportListItem[];
    errors: string[];
    loading: boolean;
    page: number;
    perPage: number;
    total: number;
    searchTerm: string;
    sortField: string;
    sortOrder: SortOrder;
    fetchReports: (overridePage?: number) => void;
    setPage: (page: number) => void;
    setPerPage: (perPage: number) => void;
    setSearchTerm: (term: string) => void;
    setSort: (field: string) => void;
}>({
    reports: [],
    errors: [],
    loading: false,
    page: 1,
    perPage: 15,
    total: 0,
    searchTerm: "",
    sortField: "created_at",
    sortOrder: "desc",
    fetchReports: () => { },
    setPage: () => { },
    setPerPage: () => { },
    setSearchTerm: () => { },
    setSort: () => { },
});

function useProviderNonConformanceReports() {
    const [reports, setReports] = useState<NonConformanceReportListItem[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPageInternal] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTermInternal] = useState("");
    const [sortField, setSortField] = useState("created_at");
    const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
    const apiService = useApiService();

    function setSearchTerm(term: string) {
        setSearchTermInternal(term);
        setPageInternal(1);
    }

    function setPage(p: number) {
        setPageInternal(p);
    }

    function setSort(field: string) {
        if (field === sortField) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
        setPageInternal(1);
    }

    useEffect(() => {
        fetchReports();
    }, [page, perPage, searchTerm, sortField, sortOrder]);

    async function fetchReports(overridePage?: number) {
        const pageToFetch = overridePage ?? page;
        setLoading(true);
        setErrors([]);

        try {
            const params = new URLSearchParams({
                page: String(pageToFetch),
                per_page: String(perPage),
                sort: sortField,
                order: sortOrder,
            });
            if (searchTerm.trim()) {
                params.set("q", searchTerm.trim());
            }
            const response = await apiService.get(
                `/non-conformance?${params.toString()}`
            );
            if (!response.ok) {
                setErrors(["Loading non-conformance reports failed. Please try again."]);
                return;
            }
            const json = await response.json();
            const data: NonConformanceReportListItem[] = (json.data ?? json).map(parseReportListItem);
            setTotal(json.meta?.total ?? data.length);
            setReports(data);
        } catch (error) {
            console.error(error);
            setErrors(["Loading non-conformance reports failed. Please try again."]);
        } finally {
            setLoading(false);
        }
    }

    return {
        reports,
        errors,
        loading,
        page,
        perPage,
        total,
        searchTerm,
        sortField,
        sortOrder,
        fetchReports,
        setPage,
        setPerPage,
        setSearchTerm,
        setSort,
    };
}

export function ProviderNonConformanceReports({ children }: { children: React.ReactNode }) {
    const value = useProviderNonConformanceReports();
    return <reportsListContext.Provider value={value}>{children}</reportsListContext.Provider>;
}

export const useNonConformanceReports = () => useContext(reportsListContext);

const reportDetailContext = createContext<{
    report: NonConformanceReport | null;
    errors: string[];
    loading: boolean;
    fetchReport: () => void;
}>({
    report: null,
    errors: [],
    loading: false,
    fetchReport: () => { },
});

function useProviderNonConformanceReportDetail(reportId: number | null | undefined) {
    const [report, setReport] = useState<NonConformanceReport | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const apiService = useApiService();

    async function fetchReport() {
        if (!reportId) return;
        setLoading(true);
        setErrors([]);

        try {
            const response = await apiService.get(`/non-conformance/${reportId}`);
            if (!response.ok) {
                setErrors(["Loading report failed. Please try again."]);
                return;
            }
            const json = await response.json();
            setReport(parseReport(json.data ?? json));
        } catch (error) {
            console.error(error);
            setErrors(["Loading report failed. Please try again."]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (reportId) {
            fetchReport();
        } else {
            setReport(null);
            setErrors([]);
        }
    }, [reportId]);

    return {
        report,
        errors,
        loading,
        fetchReport,
    };
}

export function ProviderNonConformanceReportDetail({
    children,
    reportId,
}: {
    children: React.ReactNode;
    reportId: number | null | undefined;
}) {
    const value = useProviderNonConformanceReportDetail(reportId);
    return <reportDetailContext.Provider value={value}>{children}</reportDetailContext.Provider>;
}

export const useNonConformanceReportDetail = () => useContext(reportDetailContext);
